'use client';

import { type ChangeEvent, memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { SearchIcon, XIcon } from '@air/design-icons';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { IconButton } from '~/components/IconButton';
import { Input } from '~/components/Input';
import { isUrlOutOfSyncWithInput } from '~/utils/searchInputSync';

export type SearchInputProps = {
  /** Current search value */
  value: string;
  /** Callback when search value changes (debounced) */
  onSearchChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
};

export const SearchInput = memo(
  ({ value, onSearchChange, placeholder = 'Search...', debounceMs = 300 }: SearchInputProps) => {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [localValue, setLocalValue] = useState(value);
    const localValueRef = useRef(localValue);

    localValueRef.current = localValue;

    const [debouncedSearch, , flushDebouncedSearch] = useDebouncedCallback(onSearchChange, debounceMs, {
      trailing: true,
    });

    // Sync when the URL-backed `value` changes — read local input via ref so
    // keystrokes don't retrigger this effect and overwrite in-progress edits.
    useEffect(() => {
      const inputValue = localValueRef.current;

      if (isUrlOutOfSyncWithInput(value, inputValue)) {
        return;
      }

      if (value !== inputValue) {
        setLocalValue(value);
      }
    }, [value]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        debouncedSearch(newValue);
      },
      [debouncedSearch],
    );

    const handleClear = useCallback(() => {
      setLocalValue('');
      debouncedSearch('');
      flushDebouncedSearch();
      inputRef.current?.focus();
    }, [debouncedSearch, flushDebouncedSearch]);

    const handleBlur = useCallback(() => {
      flushDebouncedSearch();
    }, [flushDebouncedSearch]);

    return (
      <div className="relative flex items-center">
        <VisuallyHidden>
          <label htmlFor={inputId}>Search</label>
        </VisuallyHidden>
        <SearchIcon className="pointer-events-none absolute left-3 text-neutral-12" size="sm" />
        <Input
          className="w-full rounded-none bg-neutral-1 px-10"
          id={inputId}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          value={localValue}
        />
        {!!localValue && (
          <IconButton aria-label="Clear search" className="absolute right-2" onClick={handleClear} size="small">
            <XIcon size="sm" />
          </IconButton>
        )}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
